import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useBillingSettings } from "@/contexts/BillingSettingsContext";
import StripeCredentialsSection from "@/components/settings/StripeCredentialsSection";

import { devLog } from "@/lib/devLog";
// Common ISO 4217 codes with their display symbol. "Custom" lets the user
// type any symbol (e.g. "kr" for SEK/NOK/DKK, "₨" for LKR/PKR, etc).
const CURRENCIES = [
  { code: "INR", symbol: "₹", label: "Indian Rupee (INR · ₹)" },
  { code: "USD", symbol: "$", label: "US Dollar (USD · $)" },
  { code: "EUR", symbol: "€", label: "Euro (EUR · €)" },
  { code: "GBP", symbol: "£", label: "British Pound (GBP · £)" },
  { code: "AED", symbol: "د.إ", label: "UAE Dirham (AED · د.إ)" },
  { code: "SGD", symbol: "S$", label: "Singapore Dollar (SGD · S$)" },
  { code: "AUD", symbol: "A$", label: "Australian Dollar (AUD · A$)" },
  { code: "CAD", symbol: "C$", label: "Canadian Dollar (CAD · C$)" },
  { code: "MYR", symbol: "RM", label: "Malaysian Ringgit (MYR · RM)" },
  { code: "CUSTOM", symbol: "", label: "Custom (type your own)" },
];

export default function PaymentsTab() {
  const { refresh: refreshBilling } = useBillingSettings();
  const emptyForm = {
    company_name: "", logo_url: "", address: "",
    gst_number: "", registration_number: "",
    contact_email: "", contact_phone: "",
    bank: { account_name: "", account_number: "", bank_name: "", ifsc: "", branch: "" },
    upi_id: "", upi_payee_name: "",
    upi_enabled: true, stripe_enabled: false, razorpay_enabled: false, bank_transfer_enabled: true,
    razorpay_key_id: "", razorpay_key_secret: "", razorpay_webhook_secret: "",
    razorpay_key_secret_set: false, razorpay_key_secret_hint: "",
    razorpay_webhook_secret_set: false, razorpay_webhook_secret_hint: "",
    tax_label: "GST",
    currency_code: "INR", currency_symbol: "₹",
    receipt_notes: "",
  };
  const [form, setForm] = useState(emptyForm);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/payments/settings/billing");
      setForm({ ...emptyForm, ...data, bank: { ...emptyForm.bank, ...(data.bank || {}) } });
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoaded(true); }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setBank = (k, v) => setForm(f => ({ ...f, bank: { ...f.bank, [k]: v } }));

  const save = async () => {
    if (form.upi_enabled && (!form.upi_id.trim() || !form.upi_payee_name.trim())) {
      toast.error("UPI ID and payee name are required when UPI is enabled");
      return;
    }
    // Remember the currency BEFORE the save so we can detect whether it changed
    // and force a full reload — that's the only reliable way to refresh every
    // already-rendered `fmtMoney(n)` call on the page.
    let prevCurrency = null;
    try {
      const { data } = await api.get("/payments/settings/billing");
      prevCurrency = `${data.currency_code}::${data.currency_symbol}`;
    } catch (err) { devLog("previous billing settings not found (first save)", err); }

    setSaving(true);
    try {
      await api.put("/payments/settings/billing", form);
      const nextCurrency = `${form.currency_code}::${form.currency_symbol}`;
      if (prevCurrency !== null && prevCurrency !== nextCurrency) {
        // Currency changed — reload so every dashboard, list, badge, and chart
        // re-formats consistently. Small delay so the toast is visible first.
        toast.success("Currency updated — reloading page to refresh all amounts…");
        setTimeout(() => window.location.reload(), 1000);
        return;
      }
      toast.success("Billing & payment settings saved");
      load();
      refreshBilling();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      {/* Company / Bill From */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Bill-from &amp; company details</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">These appear as the letterhead on every invoice and receipt PDF.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 col-span-1 sm:col-span-2"><Label>Company / centre name</Label><Input value={form.company_name} onChange={e => set("company_name", e.target.value)} data-testid="bs-company-name" disabled={!loaded} /></div>
            <div className="space-y-1.5 col-span-1 sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} data-testid="bs-address" disabled={!loaded} /></div>
            <div className="space-y-1.5"><Label>GST number</Label><Input value={form.gst_number} onChange={e => set("gst_number", e.target.value.toUpperCase())} placeholder="29AAAAA0000A1Z5" data-testid="bs-gst" disabled={!loaded} /></div>
            <div className="space-y-1.5"><Label>Registration / licence number</Label><Input value={form.registration_number} onChange={e => set("registration_number", e.target.value)} data-testid="bs-regno" disabled={!loaded} /></div>
            <div className="space-y-1.5"><Label>Contact email</Label><Input type="email" value={form.contact_email} onChange={e => set("contact_email", e.target.value)} data-testid="bs-email" disabled={!loaded} /></div>
            <div className="space-y-1.5"><Label>Contact phone</Label><Input value={form.contact_phone} onChange={e => set("contact_phone", e.target.value)} data-testid="bs-phone" disabled={!loaded} /></div>
            <div className="space-y-1.5 col-span-1 sm:col-span-2"><Label>Logo URL <span className="text-[10px] text-muted-foreground">(optional)</span></Label><Input value={form.logo_url} onChange={e => set("logo_url", e.target.value)} placeholder="https://…/logo.png" disabled={!loaded} /></div>
            <div className="space-y-1.5"><Label>Tax label</Label><Input value={form.tax_label} onChange={e => set("tax_label", e.target.value)} placeholder="GST" disabled={!loaded} /></div>
          </div>
        </CardContent>
      </Card>

      {/* Currency */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Currency</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Used on every invoice, receipt PDF and dashboard money display.
            <span className="block mt-1">Note: UPI QR payments are always processed in INR — this is a hard requirement of the UPI protocol.</span>
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Currency</Label>
              <Select
                value={CURRENCIES.find(c => c.code === form.currency_code) ? form.currency_code : "CUSTOM"}
                onValueChange={(code) => {
                  if (code === "CUSTOM") {
                    setForm(f => ({ ...f, currency_code: "" }));
                  } else {
                    const found = CURRENCIES.find(c => c.code === code);
                    setForm(f => ({ ...f, currency_code: code, currency_symbol: found?.symbol || f.currency_symbol }));
                  }
                }}
                disabled={!loaded}
              >
                <SelectTrigger data-testid="bs-currency-code"><SelectValue placeholder="Select currency" /></SelectTrigger>
                <SelectContent className="bg-card">
                  {CURRENCIES.map(c => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Symbol shown on invoices</Label>
              <Input
                value={form.currency_symbol}
                onChange={e => set("currency_symbol", e.target.value)}
                placeholder="₹"
                data-testid="bs-currency-symbol"
                disabled={!loaded}
                maxLength={5}
                className="font-mono"
              />
            </div>
            {!CURRENCIES.find(c => c.code === form.currency_code && c.code !== "CUSTOM") && (
              <div className="space-y-1.5 col-span-1 sm:col-span-2">
                <Label>Currency code (ISO 4217)</Label>
                <Input
                  value={form.currency_code}
                  onChange={e => set("currency_code", e.target.value.toUpperCase())}
                  placeholder="e.g. NPR"
                  disabled={!loaded}
                  maxLength={5}
                  className="font-mono uppercase"
                  data-testid="bs-currency-code-custom"
                />
              </div>
            )}
            <div className="col-span-1 sm:col-span-2 rounded-md border border-dashed border-border p-3 text-sm">
              <div className="text-[11px] text-muted-foreground mb-1">Preview on invoice total</div>
              <div className="font-mono">Total ({form.currency_code || "—"}) &nbsp; {form.currency_symbol || "?"} 1,699.50</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment methods */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Payment methods</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Toggle which options appear to guardians on the Pay-online screen.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* UPI */}
          <div className="border border-border rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">UPI (QR code)</div>
                <div className="text-[11px] text-muted-foreground">Native India rail — no gateway fees, no currency conversion.</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.upi_enabled} onCheckedChange={v => set("upi_enabled", !!v)} data-testid="bs-upi-enabled" />
                <span>{form.upi_enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            {form.upi_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                <div className="space-y-1.5"><Label>UPI ID</Label><Input value={form.upi_id} onChange={e => set("upi_id", e.target.value)} placeholder="therapyflow@icici" data-testid="bs-upi-id" disabled={!loaded} /></div>
                <div className="space-y-1.5"><Label>Payee display name</Label><Input value={form.upi_payee_name} onChange={e => set("upi_payee_name", e.target.value)} placeholder="TherapyFlow Centre" data-testid="bs-upi-payee" disabled={!loaded} /></div>
              </div>
            )}
          </div>

          {/* Stripe */}
          <div className="border border-border rounded-md p-4 space-y-2">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Stripe (card / international)</div>
                <div className="text-[11px] text-muted-foreground">Collects card payments from your parents / clients directly into your own Stripe account. Configure your workspace's Test + Live credentials below.</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.stripe_enabled} onCheckedChange={v => set("stripe_enabled", !!v)} data-testid="bs-stripe-enabled" />
                <span>{form.stripe_enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
          </div>

          {/* iter-61 — Per-workspace Stripe Collection Credentials.
              Shown ONLY when Stripe Payment Enable is on. Backend gates
              read/write to owner+admin, so non-admins never see this
              section even if the DOM is inspected. */}
          {form.stripe_enabled && (
            <StripeCredentialsSection stripeEnabled={form.stripe_enabled} />
          )}

          {/* Razorpay */}
          <div className="border border-border rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Razorpay (card / UPI / netbanking / wallets)</div>
                <div className="text-[11px] text-muted-foreground">
                  Full-service payment gateway for India. Accepts cards, UPI, netbanking and wallets in one modal. Uses your own Razorpay account.
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.razorpay_enabled} onCheckedChange={v => set("razorpay_enabled", !!v)} data-testid="bs-razorpay-enabled" />
                <span>{form.razorpay_enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            {form.razorpay_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                <div className="space-y-1.5 sm:col-span-2">
                  <Label>Razorpay Key ID</Label>
                  <Input
                    value={form.razorpay_key_id}
                    onChange={e => set("razorpay_key_id", e.target.value.trim())}
                    placeholder="rzp_test_... or rzp_live_..."
                    className="font-mono"
                    data-testid="bs-razorpay-key-id"
                    disabled={!loaded}
                  />
                  <p className="text-[10px] text-muted-foreground">Public identifier — safe to expose to the browser.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Razorpay Key Secret</Label>
                  <Input
                    type="password"
                    value={form.razorpay_key_secret}
                    onChange={e => set("razorpay_key_secret", e.target.value)}
                    placeholder={form.razorpay_key_secret_set ? `Configured (•••• ${form.razorpay_key_secret_hint})` : "Paste secret"}
                    className="font-mono"
                    data-testid="bs-razorpay-key-secret"
                    disabled={!loaded}
                    autoComplete="new-password"
                  />
                  <p className="text-[10px] text-muted-foreground">Leave blank to keep the existing value.</p>
                </div>
                <div className="space-y-1.5">
                  <Label>Webhook Secret <span className="text-[10px] text-muted-foreground">(optional)</span></Label>
                  <Input
                    type="password"
                    value={form.razorpay_webhook_secret}
                    onChange={e => set("razorpay_webhook_secret", e.target.value)}
                    placeholder={form.razorpay_webhook_secret_set ? `Configured (•••• ${form.razorpay_webhook_secret_hint})` : "Paste webhook secret"}
                    className="font-mono"
                    data-testid="bs-razorpay-webhook-secret"
                    disabled={!loaded}
                    autoComplete="new-password"
                  />
                  <p className="text-[10px] text-muted-foreground">Add this after you register the webhook URL below in your Razorpay Dashboard.</p>
                </div>
                <div className="sm:col-span-2 rounded-md bg-muted/40 border border-border p-3 text-[11px] space-y-1">
                  <div className="font-medium text-foreground">Setup steps:</div>
                  <ol className="list-decimal ml-4 space-y-0.5 text-muted-foreground">
                    <li>Sign in at <span className="font-mono">dashboard.razorpay.com</span> → Settings → API Keys → Generate Test/Live Key. Paste both values above.</li>
                    <li>(Optional) Settings → Webhooks → Add Webhook → URL:
                      <span className="ml-1 font-mono break-all bg-muted px-1 rounded">
                        {(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api/webhook/razorpay
                      </span>
                      → Events: <span className="font-mono">payment.captured</span>, <span className="font-mono">payment.authorized</span>. Copy the shown webhook secret into the field above.</li>
                    <li>Click <span className="font-medium">Save billing settings</span>. Guardians will immediately see the Razorpay payment button on unpaid invoices.</li>
                  </ol>
                </div>
              </div>
            )}
          </div>

          {/* Bank transfer */}
          <div className="border border-border rounded-md p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium">Bank transfer (NEFT / IMPS / RTGS)</div>
                <div className="text-[11px] text-muted-foreground">Details are printed on unpaid invoice PDFs so guardians can transfer manually.</div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.bank_transfer_enabled} onCheckedChange={v => set("bank_transfer_enabled", !!v)} data-testid="bs-bank-enabled" />
                <span>{form.bank_transfer_enabled ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            {form.bank_transfer_enabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
                <div className="space-y-1.5"><Label>Account holder name</Label><Input value={form.bank.account_name} onChange={e => setBank("account_name", e.target.value)} data-testid="bs-bank-name" disabled={!loaded} /></div>
                <div className="space-y-1.5"><Label>Bank name</Label><Input value={form.bank.bank_name} onChange={e => setBank("bank_name", e.target.value)} data-testid="bs-bank-bank" disabled={!loaded} /></div>
                <div className="space-y-1.5"><Label>Account number</Label><Input value={form.bank.account_number} onChange={e => setBank("account_number", e.target.value)} data-testid="bs-bank-acct" disabled={!loaded} /></div>
                <div className="space-y-1.5"><Label>IFSC code</Label><Input value={form.bank.ifsc} onChange={e => setBank("ifsc", e.target.value.toUpperCase())} placeholder="ICIC0000123" data-testid="bs-bank-ifsc" disabled={!loaded} /></div>
                <div className="space-y-1.5 col-span-1 sm:col-span-2"><Label>Branch (optional)</Label><Input value={form.bank.branch} onChange={e => setBank("branch", e.target.value)} disabled={!loaded} /></div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Receipt notes */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-display tracking-tight">Payment receipt notes</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">Appears at the bottom of PAID-receipt PDFs.</p>
        </CardHeader>
        <CardContent>
          <textarea
            value={form.receipt_notes}
            onChange={e => set("receipt_notes", e.target.value)}
            rows={3}
            className="w-full text-sm rounded-md border border-border p-3 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
            placeholder="Thank you for choosing us. This receipt is proof of payment; please retain it for your records…"
            data-testid="bs-receipt-notes"
            disabled={!loaded}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving || !loaded} data-testid="save-billing-settings" size="lg" className="gap-1.5">
          {saving ? "Saving…" : "Save billing settings"}
        </Button>
      </div>
    </div>
  );
}
